import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  AudioLines,
  BookOpen,
  Check,
  ChevronDown,
  Clock,
  Copy,
  CreditCard,
  Crown,
  Download,
  Globe,
  FileAudio,
  FileText,
  Flame,
  Gauge,
  Hash,
  HelpCircle,
  Layers,
  Lightbulb,
  ListOrdered,
  Loader2,
  LogOut,
  Mic,
  Pencil,
  Quote,
  RefreshCw,
  RotateCcw,
  Route,
  Scale,
  ShieldCheck,
  Sparkles,
  Target,
  TrendingUp,
  Upload,
  Users,
  Wand2,
  WifiOff,
  X,
  Zap,
} from "lucide-react";

import {
  ARCHETYPES,
  AUDIENCES,
  AccentId,
  ArchetypeId,
  AudienceId,
  LENGTHS,
  LINKEDIN_LIMIT,
  LengthId,
  SAMPLE_TRANSCRIPT,
  TONES,
  ToneId,
  countWords,
  generateLocalPosts,
} from "@casttolink/core";

import type { Session, SupabaseClient } from "@supabase/supabase-js";

import { SignIn } from "./components/SignIn";
import {
  ApiError,
  type Account,
  type Capabilities,
  apiUrl,
  fetchAccount,
  fetchStatus,
  postGenerate,
  startPayfastCheckout,
  startPaypalCheckout,
  submitCheckout,
} from "./lib/api";
import { supabaseFor } from "./lib/supabase";

/* -------------------------------------------------------------------------- */
/*  Presentation tables: the parts of an archetype that only the UI needs      */
/* -------------------------------------------------------------------------- */

const ICONS: Record<ArchetypeId, typeof Zap> = {
  "hook-insights-cta": Zap,
  contrarian: Flame,
  story: BookOpen,
  framework: Route,
  myths: Scale,
  "data-drop": Gauge,
  "pull-quote": Quote,
  mistake: AlertTriangle,
  "rapid-fire": ListOrdered,
  "open-question": HelpCircle,
};

const ACCENTS: Record<
  AccentId,
  { text: string; chip: string; border: string; glow: string }
> = {
  emerald: {
    text: "text-emerald-300",
    chip: "bg-emerald-500/10",
    border: "border-emerald-400/25",
    glow: "from-emerald-500/60",
  },
  indigo: {
    text: "text-indigo-300",
    chip: "bg-indigo-500/10",
    border: "border-indigo-400/25",
    glow: "from-indigo-500/60",
  },
  cyan: {
    text: "text-cyan-300",
    chip: "bg-cyan-500/10",
    border: "border-cyan-400/25",
    glow: "from-cyan-500/60",
  },
  violet: {
    text: "text-violet-300",
    chip: "bg-violet-500/10",
    border: "border-violet-400/25",
    glow: "from-violet-500/60",
  },
  amber: {
    text: "text-amber-300",
    chip: "bg-amber-500/10",
    border: "border-amber-400/25",
    glow: "from-amber-500/60",
  },
  rose: {
    text: "text-rose-300",
    chip: "bg-rose-500/10",
    border: "border-rose-400/25",
    glow: "from-rose-500/60",
  },
  sky: {
    text: "text-sky-300",
    chip: "bg-sky-500/10",
    border: "border-sky-400/25",
    glow: "from-sky-500/60",
  },
  teal: {
    text: "text-teal-300",
    chip: "bg-teal-500/10",
    border: "border-teal-400/25",
    glow: "from-teal-500/60",
  },
  fuchsia: {
    text: "text-fuchsia-300",
    chip: "bg-fuchsia-500/10",
    border: "border-fuchsia-400/25",
    glow: "from-fuchsia-500/60",
  },
  lime: {
    text: "text-lime-300",
    chip: "bg-lime-500/10",
    border: "border-lime-400/25",
    glow: "from-lime-500/60",
  },
};

const LIVE_STAGES = [
  "Sending the transcript to Claude",
  "Reading the episode and ranking the claims",
  "Mapping beats to 10 LinkedIn archetypes",
  "Writing the posts in your selected tone",
  "Checking every draft against the 1,300-character limit",
];

const DEMO_STAGES = [
  "Chunking transcript into narrative beats",
  "Scoring hooks and extracting key claims",
  "Mapping beats to 10 LinkedIn archetypes",
  "Drafting posts from the offline templates",
  "Trimming every draft under 1,300 characters",
];

interface GeneratedPost {
  id: ArchetypeId;
  index: number;
  label: string;
  note: string;
  accent: AccentId;
  icon: typeof Zap;
  text: string;
  original: string;
  origin: "claude" | "local";
}

type Notice = { kind: "info" | "warn"; text: string } | null;

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                   */
/* -------------------------------------------------------------------------- */

function modelLabel(id: string) {
  const match = id.match(/^claude-(opus|sonnet|haiku|fable|mythos)-(\d+)(?:-(\d+))?/i);
  if (!match) return id;
  const family = match[1].charAt(0).toUpperCase() + match[1].slice(1);
  return `${family} ${match[2]}${match[3] ? `.${match[3]}` : ""}`;
}

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function toPosts(
  drafted: { id: ArchetypeId; text: string; origin?: "claude" | "local" }[],
): GeneratedPost[] {
  const byId = new Map(drafted.map((post) => [post.id, post]));

  return ARCHETYPES.map((archetype, index) => {
    const draft = byId.get(archetype.id);
    const text = draft?.text ?? "";
    return {
      id: archetype.id,
      index: index + 1,
      label: archetype.label,
      note: archetype.note,
      accent: archetype.accent,
      icon: ICONS[archetype.id],
      text,
      original: text,
      origin: draft?.origin ?? "local",
    };
  }).filter((post) => post.text.length > 0);
}

async function writeClipboard(value: string) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    /* fall through to the legacy path */
  }

  try {
    const area = document.createElement("textarea");
    area.value = value;
    area.setAttribute("readonly", "true");
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(area);
    return ok;
  } catch {
    return false;
  }
}

/* -------------------------------------------------------------------------- */
/*  Presentational pieces                                                     */
/* -------------------------------------------------------------------------- */

function StatusPill({
  icon: Icon,
  label,
  value,
  tone = "neutral",
}: {
  icon: typeof Zap;
  label: string;
  value: string;
  tone?: "neutral" | "good" | "muted";
}) {
  return (
    <div className="hidden items-center gap-2 rounded-lg border border-zinc-800/80 bg-zinc-900/60 px-2.5 py-1.5 lg:flex">
      <Icon
        className={`h-3.5 w-3.5 ${
          tone === "good" ? "text-emerald-400" : tone === "muted" ? "text-amber-400" : "text-zinc-500"
        }`}
      />
      <span className="text-[11px] font-medium uppercase tracking-wider text-zinc-500">
        {label}
      </span>
      <span className="font-mono text-[11px] text-zinc-300">{value}</span>
    </div>
  );
}

function Select<T extends string>({
  icon: Icon,
  label,
  hint,
  value,
  options,
  onChange,
}: {
  icon: typeof Zap;
  label: string;
  hint?: string;
  value: T;
  options: { id: T; label: string }[];
  onChange: (value: T) => void;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </span>
      <div className="group relative">
        <select
          value={value}
          onChange={(event) => onChange(event.target.value as T)}
          className="w-full cursor-pointer appearance-none rounded-xl border border-zinc-800 bg-zinc-900/80 px-3.5 py-2.5 pr-10 text-sm font-medium text-zinc-100 outline-none transition hover:border-zinc-700 focus:border-emerald-500/60 focus:ring-2 focus:ring-emerald-500/20"
        >
          {options.map((option) => (
            <option key={option.id} value={option.id} className="bg-zinc-900">
              {option.label}
            </option>
          ))}
        </select>
        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500 transition group-hover:text-zinc-300" />
      </div>
      {hint ? <span className="mt-1.5 block text-xs text-zinc-500">{hint}</span> : null}
    </label>
  );
}

function CharacterMeter({ text }: { text: string }) {
  const chars = text.length;
  const pct = Math.min(100, (chars / LINKEDIN_LIMIT) * 100);
  const over = chars > LINKEDIN_LIMIT;
  const near = !over && chars > LINKEDIN_LIMIT * 0.85;

  const barColor = over ? "bg-rose-500" : near ? "bg-amber-400" : "bg-emerald-400";
  const textColor = over ? "text-rose-400" : near ? "text-amber-300" : "text-emerald-300";

  return (
    <div className="flex items-center gap-2.5">
      <div className="h-1.5 w-16 overflow-hidden rounded-full bg-zinc-800 sm:w-24">
        <div
          className={`h-full rounded-full transition-all duration-500 ${barColor}`}
          style={{ width: `${Math.max(pct, 3)}%` }}
        />
      </div>
      <span className={`font-mono text-[11px] tabular-nums ${textColor}`}>
        {chars.toLocaleString()}/{LINKEDIN_LIMIT.toLocaleString()}
      </span>
    </div>
  );
}

type CopyFlash = "ok" | "fail" | null;

function PostCard({
  post,
  isEditing,
  flash,
  showOrigin,
  onEditToggle,
  onChange,
  onReset,
  onCopy,
}: {
  post: GeneratedPost;
  isEditing: boolean;
  flash: CopyFlash;
  showOrigin: boolean;
  onEditToggle: () => void;
  onChange: (value: string) => void;
  onReset: () => void;
  onCopy: () => void;
}) {
  const accent = ACCENTS[post.accent];
  const Icon = post.icon;
  const edited = post.text !== post.original;
  const over = post.text.length > LINKEDIN_LIMIT;
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      const node = textareaRef.current;
      node.focus();
      node.style.height = "auto";
      node.style.height = `${node.scrollHeight}px`;
    }
  }, [isEditing]);

  return (
    <article
      className="group relative animate-fade-up overflow-hidden rounded-2xl border border-zinc-800/80 bg-zinc-900/40 transition hover:border-zinc-700 hover:bg-zinc-900/70"
      style={{ animationDelay: `${post.index * 45}ms` }}
    >
      <div
        className={`pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r to-transparent opacity-70 ${accent.glow}`}
      />

      <header className="flex flex-wrap items-center gap-3 border-b border-zinc-800/60 px-4 py-3 sm:px-5">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-zinc-800 bg-zinc-950 font-mono text-[11px] text-zinc-500">
          {String(post.index).padStart(2, "0")}
        </span>

        <div
          className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1 ${accent.chip} ${accent.border}`}
          title={post.note}
        >
          <Icon className={`h-3.5 w-3.5 ${accent.text}`} />
          <span className={`text-xs font-semibold ${accent.text}`}>{post.label}</span>
        </div>

        {showOrigin && post.origin === "local" ? (
          <span className="flex items-center gap-1 rounded-md border border-amber-400/20 bg-amber-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-300">
            <WifiOff className="h-3 w-3" />
            Offline draft
          </span>
        ) : null}

        {edited ? (
          <span className="rounded-md border border-zinc-700/60 bg-zinc-800/60 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-400">
            Edited
          </span>
        ) : null}

        <div className="ml-auto">
          <CharacterMeter text={post.text} />
        </div>
      </header>

      <div className="px-4 py-4 sm:px-5">
        {isEditing ? (
          <textarea
            ref={textareaRef}
            value={post.text}
            onChange={(event) => {
              onChange(event.target.value);
              const node = event.target;
              node.style.height = "auto";
              node.style.height = `${node.scrollHeight}px`;
            }}
            onKeyDown={(event) => {
              if (
                ((event.metaKey || event.ctrlKey) && event.key === "Enter") ||
                event.key === "Escape"
              ) {
                event.preventDefault();
                onEditToggle();
              }
            }}
            spellCheck
            className="min-h-[220px] w-full resize-none rounded-xl border border-emerald-500/30 bg-zinc-950/80 p-3.5 text-[13.5px] leading-relaxed text-zinc-100 outline-none transition focus:border-emerald-500/60 focus:ring-2 focus:ring-emerald-500/20"
          />
        ) : (
          <p className="whitespace-pre-wrap text-[13.5px] leading-relaxed text-zinc-300">
            {post.text}
          </p>
        )}
      </div>

      <footer className="flex flex-wrap items-center gap-2 border-t border-zinc-800/60 bg-zinc-950/40 px-4 py-2.5 sm:px-5">
        <span className="flex items-center gap-1.5 text-[11px] text-zinc-500">
          <Hash className="h-3 w-3" />
          {countWords(post.text)} words
        </span>
        <span className="hidden items-center gap-1.5 text-[11px] text-zinc-500 sm:flex">
          <Clock className="h-3 w-3" />~
          {Math.max(1, Math.round(countWords(post.text) / 200))} min read
        </span>
        {over ? (
          <span className="flex items-center gap-1.5 text-[11px] font-medium text-rose-400">
            <AlertTriangle className="h-3 w-3" />
            Over the LinkedIn limit
          </span>
        ) : (
          <span className="flex items-center gap-1.5 text-[11px] text-emerald-400/80">
            <ShieldCheck className="h-3 w-3" />
            Fits LinkedIn
          </span>
        )}

        <div className="ml-auto flex items-center gap-1.5">
          {edited ? (
            <button
              type="button"
              onClick={onReset}
              title="Revert to the generated draft"
              className="flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-[11px] font-medium text-zinc-500 transition hover:bg-zinc-800/80 hover:text-zinc-200"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Revert
            </button>
          ) : null}

          <button
            type="button"
            onClick={onEditToggle}
            className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[11px] font-semibold transition ${
              isEditing
                ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25"
                : "border-zinc-800 bg-zinc-900 text-zinc-300 hover:border-zinc-700 hover:text-zinc-100"
            }`}
          >
            {isEditing ? <Check className="h-3.5 w-3.5" /> : <Pencil className="h-3.5 w-3.5" />}
            {isEditing ? "Done" : "Edit"}
          </button>

          <button
            type="button"
            onClick={onCopy}
            title={
              flash === "fail"
                ? "Your browser blocked clipboard access — select the text and copy manually"
                : "Copy this post"
            }
            className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[11px] font-semibold transition ${
              flash === "ok"
                ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-300"
                : flash === "fail"
                  ? "border-rose-500/40 bg-rose-500/15 text-rose-300"
                  : "border-zinc-800 bg-zinc-900 text-zinc-300 hover:border-zinc-700 hover:text-zinc-100"
            }`}
          >
            {flash === "ok" ? (
              <Check className="h-3.5 w-3.5" />
            ) : flash === "fail" ? (
              <AlertTriangle className="h-3.5 w-3.5" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
            {flash === "ok" ? "Copied!" : flash === "fail" ? "Blocked" : "Copy"}
          </button>
        </div>
      </footer>
    </article>
  );
}

function SkeletonCard({ delay }: { delay: number }) {
  return (
    <div
      className="animate-fade-up overflow-hidden rounded-2xl border border-zinc-800/80 bg-zinc-900/40"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="flex items-center gap-3 border-b border-zinc-800/60 px-5 py-3">
        <div className="h-7 w-7 rounded-lg bg-zinc-800/80" />
        <div className="h-6 w-40 rounded-lg bg-zinc-800/80" />
        <div className="ml-auto h-1.5 w-24 rounded-full bg-zinc-800/80" />
      </div>
      <div className="space-y-2.5 px-5 py-5">
        {[92, 100, 74, 96, 60].map((width, index) => (
          <div
            key={index}
            className="relative h-3 overflow-hidden rounded-full bg-zinc-800/70"
            style={{ width: `${width}%` }}
          >
            <div className="absolute inset-0 animate-shimmer bg-gradient-to-r from-transparent via-zinc-700/70 to-transparent" />
          </div>
        ))}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Page                                                                      */
/* -------------------------------------------------------------------------- */

export default function App() {
  const [bannerOpen, setBannerOpen] = useState(true);
  const [caps, setCaps] = useState<Capabilities | null>(null);
  const [supabase, setSupabase] = useState<SupabaseClient | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [account, setAccount] = useState<Account | null>(null);

  const [file, setFile] = useState<{ name: string; size: number } | null>(null);
  const [uploadState, setUploadState] = useState<
    "idle" | "uploading" | "transcribing" | "ready"
  >("idle");
  const [progress, setProgress] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);

  const [pasteOpen, setPasteOpen] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [transcriptSource, setTranscriptSource] = useState<
    "none" | "audio" | "sample" | "paste"
  >("none");

  const [toneId, setToneId] = useState<ToneId>("leader");
  const [audienceId, setAudienceId] = useState<AudienceId>("founders");
  const [lengthId, setLengthId] = useState<LengthId>("balanced");

  const [genState, setGenState] = useState<"idle" | "running" | "done">("idle");
  const [stage, setStage] = useState(0);
  const [posts, setPosts] = useState<GeneratedPost[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [copyFlash, setCopyFlash] = useState<{ id: string; ok: boolean } | null>(null);
  const [allFlash, setAllFlash] = useState<boolean | null>(null);
  const [runs, setRuns] = useState(0);
  const [notice, setNotice] = useState<Notice>(null);
  const [billingOpen, setBillingOpen] = useState(false);
  const [billingBusy, setBillingBusy] = useState<"payfast" | "paypal" | null>(null);
  const [usage, setUsage] = useState<{ inputTokens: number; outputTokens: number } | null>(null);
  const [usedModel, setUsedModel] = useState<string | null>(null);

  const uploadTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const stageTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const allTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const requestRef = useRef<XMLHttpRequest | null>(null);

  const tone = useMemo(() => TONES.find((item) => item.id === toneId) ?? TONES[0], [toneId]);
  const audience = useMemo(
    () => AUDIENCES.find((item) => item.id === audienceId) ?? AUDIENCES[0],
    [audienceId],
  );
  const length = useMemo(
    () => LENGTHS.find((item) => item.id === lengthId) ?? LENGTHS[1],
    [lengthId],
  );

  const transcriptWords = useMemo(() => countWords(transcript), [transcript]);
  const hasSource = transcriptWords >= 40;
  const busy = uploadState === "uploading" || uploadState === "transcribing";
  const canGenerate = hasSource && genState !== "running" && !busy;
  const liveGeneration = caps?.generation ?? false;
  const liveTranscription = caps?.transcription ?? false;
  const stages = liveGeneration ? LIVE_STAGES : DEMO_STAGES;

  useEffect(() => {
    const controller = new AbortController();

    fetchStatus(controller.signal)
      .then((data) => setCaps(data))
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError") return;
        setCaps({
          transcription: false,
          generation: false,
          model: "claude-sonnet-5-5",
          speechModel: "nova-3",
          auth: null,
        });
      });

    return () => controller.abort();
  }, []);

  // The Supabase client is built from whatever /api/status reported, so the web and
  // mobile bundles never carry their own copy of the project settings.
  useEffect(() => {
    if (!caps?.auth) return;

    const client = supabaseFor(caps.auth.url, caps.auth.anonKey);
    setSupabase(client);

    client.auth.getSession().then(({ data }) => setSession(data.session));
    const { data } = client.auth.onAuthStateChange((_event, next) => setSession(next));

    return () => data.subscription.unsubscribe();
  }, [caps]);

  useEffect(() => {
    const token = session?.access_token;
    if (!token) {
      setAccount(null);
      return;
    }

    const controller = new AbortController();
    fetchAccount(token, controller.signal)
      .then(setAccount)
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError") return;
        setAccount(null);
      });

    return () => controller.abort();
  }, [session]);

  useEffect(
    () => () => {
      if (uploadTimer.current) clearInterval(uploadTimer.current);
      if (stageTimer.current) clearInterval(stageTimer.current);
      if (copyTimer.current) clearTimeout(copyTimer.current);
      if (allTimer.current) clearTimeout(allTimer.current);
      requestRef.current?.abort();
    },
    [],
  );

  const simulateUpload = useCallback(() => {
    if (uploadTimer.current) clearInterval(uploadTimer.current);

    uploadTimer.current = setInterval(() => {
      setProgress((current) => {
        const next = current + 3 + Math.random() * 11;
        if (next >= 100) {
          if (uploadTimer.current) clearInterval(uploadTimer.current);
          uploadTimer.current = null;
          setTimeout(() => {
            setUploadState("ready");
            setTranscript((existing) =>
              countWords(existing) >= 40 ? existing : SAMPLE_TRANSCRIPT,
            );
            setTranscriptSource("sample");
          }, 420);
          return 100;
        }
        return next;
      });
    }, 130);
  }, []);

  const transcribeFile = useCallback((incoming: File, token: string) => {
    const form = new FormData();
    form.append("file", incoming);

    const request = new XMLHttpRequest();
    requestRef.current = request;
    request.open("POST", apiUrl("/transcribe"));
    request.setRequestHeader("authorization", `Bearer ${token}`);

    // The upload bar tops out at 92% so the remaining travel covers the
    // transcription itself, which reports no progress of its own.
    request.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        setProgress(Math.min(92, (event.loaded / event.total) * 92));
      }
    };

    request.upload.onload = () => {
      setProgress(92);
      setUploadState("transcribing");
    };

    request.onload = () => {
      requestRef.current = null;
      let payload: { transcript?: string; message?: string } = {};
      try {
        payload = JSON.parse(request.responseText);
      } catch {
        /* handled below by the status check */
      }

      if (request.status >= 200 && request.status < 300 && payload.transcript) {
        setProgress(100);
        setUploadState("ready");
        setTranscript(payload.transcript);
        setTranscriptSource("audio");
        setNotice(null);
        return;
      }

      setUploadState("idle");
      setProgress(0);
      setFile(null);
      setFileError(
        payload.message ??
          `Transcription failed (${request.status}). Paste a transcript instead.`,
      );
    };

    request.onerror = () => {
      requestRef.current = null;
      setUploadState("idle");
      setProgress(0);
      setFile(null);
      setFileError("Could not reach the transcription endpoint.");
    };

    request.send(form);
  }, []);

  const startUpload = useCallback(
    (incoming: File) => {
      const name = incoming.name.toLowerCase();
      const isAudio =
        /\.(mp3|wav|m4a|aac|flac|ogg)$/.test(name) || incoming.type.startsWith("audio/");

      if (!isAudio) {
        setFileError(
          "That file type isn't supported yet — drop an .mp3 or .wav, or paste a transcript instead.",
        );
        return;
      }

      const token = session?.access_token;

      // Transcription costs money per minute, so the server requires a session.
      // Say so before uploading rather than after a 401.
      if (liveTranscription && !token) {
        setFileError("Sign in first — transcription needs an account.");
        return;
      }

      if (uploadTimer.current) clearInterval(uploadTimer.current);
      requestRef.current?.abort();

      setFileError(null);
      setFile({ name: incoming.name, size: incoming.size });
      setUploadState("uploading");
      setProgress(0);
      setGenState("idle");
      setPosts([]);

      if (liveTranscription && token) {
        transcribeFile(incoming, token);
      } else {
        simulateUpload();
      }
    },
    [liveTranscription, session, simulateUpload, transcribeFile],
  );

  const clearFile = useCallback(() => {
    if (uploadTimer.current) clearInterval(uploadTimer.current);
    uploadTimer.current = null;
    requestRef.current?.abort();
    requestRef.current = null;

    setFile(null);
    setProgress(0);
    setUploadState("idle");
    setFileError(null);
    if (transcriptSource === "audio" || transcriptSource === "sample") {
      setTranscript("");
      setTranscriptSource("none");
    }
    if (inputRef.current) inputRef.current.value = "";
  }, [transcriptSource]);

  const runGeneration = useCallback(async () => {
    if (!hasSource) return;
    if (stageTimer.current) clearInterval(stageTimer.current);

    const stageCount = (liveGeneration ? LIVE_STAGES : DEMO_STAGES).length;

    setGenState("running");
    setStage(0);
    setEditingId(null);
    setCopyFlash(null);
    setNotice(null);

    stageTimer.current = setInterval(
      () => setStage((current) => Math.min(current + 1, stageCount - 1)),
      liveGeneration ? 2600 : 460,
    );

    const finish = () => {
      if (stageTimer.current) clearInterval(stageTimer.current);
      stageTimer.current = null;
    };

    const offline = (message?: string) => {
      setPosts(
        toPosts(
          generateLocalPosts(transcript, toneId, audienceId, lengthId).map((post) => ({
            ...post,
            origin: "local" as const,
          })),
        ),
      );
      setUsedModel(null);
      setUsage(null);
      setRuns((value) => value + 1);
      setGenState("done");
      if (message) setNotice({ kind: "warn", text: message });
    };

    if (!liveGeneration) {
      // Keep the staged animation visible for a beat before the templates land.
      setTimeout(() => {
        finish();
        offline();
      }, stageCount * 460);
      return;
    }

    const token = session?.access_token;

    if (!token) {
      finish();
      setGenState("idle");
      setNotice({ kind: "info", text: "Sign in to generate — it takes one emailed code." });
      return;
    }

    try {
      const data = await postGenerate(token, {
        transcript,
        tone: toneId,
        audience: audienceId,
        length: lengthId,
        sourceKind: transcriptSource === "audio" ? "upload" : "paste",
        sourceRef: transcriptSource === "audio" ? (file?.name ?? null) : null,
      });

      finish();
      setPosts(toPosts(data.posts));
      setUsedModel(data.model ?? null);
      setUsage(data.usage ?? null);
      setRuns((value) => value + 1);
      setGenState("done");

      if (data.quota) {
        setAccount((current) => (current ? { ...current, quota: data.quota! } : current));
      }

      if (data.truncated) {
        setNotice({
          kind: "info",
          text: "The transcript was longer than the per-request cap, so only the first part was sent to Claude.",
        });
      }
    } catch (error) {
      finish();

      if (error instanceof ApiError) {
        // A used-up allowance is not a failure to paper over with offline drafts.
        if (error.status === 429) {
          if (error.body.quota) {
            setAccount((current) =>
              current ? { ...current, quota: error.body.quota! } : current,
            );
          }
          setGenState("idle");
          setNotice({ kind: "warn", text: error.message });
          return;
        }

        if (error.status === 401) {
          setGenState("idle");
          setNotice({ kind: "warn", text: "That session expired. Sign in again to generate." });
          return;
        }

        offline(`${error.message} Showing offline drafts instead.`);
        return;
      }

      offline("Could not reach the generation endpoint. Showing offline drafts instead.");
    }
  }, [
    audienceId,
    file,
    hasSource,
    lengthId,
    liveGeneration,
    session,
    toneId,
    transcript,
    transcriptSource,
  ]);

  const upgrade = useCallback(() => {
    if (!session?.access_token) {
      setNotice({ kind: "info", text: "Sign in first — then upgrading takes one more step." });
      return;
    }
    setBillingOpen(true);
  }, [session]);

  const payWith = useCallback(
    async (provider: "payfast" | "paypal") => {
      const token = session?.access_token;
      if (!token) return;

      setBillingBusy(provider);

      try {
        if (provider === "payfast") {
          submitCheckout(await startPayfastCheckout(token));
        } else {
          window.location.href = (await startPaypalCheckout(token)).approveUrl;
        }
      } catch (error) {
        setBillingBusy(null);
        setBillingOpen(false);
        setNotice({
          kind: "warn",
          text: error instanceof Error ? error.message : "Could not start the checkout.",
        });
      }
    },
    [session],
  );

  const updatePost = useCallback((id: string, text: string) => {
    setPosts((current) => current.map((post) => (post.id === id ? { ...post, text } : post)));
  }, []);

  const resetPost = useCallback((id: string) => {
    setPosts((current) =>
      current.map((post) => (post.id === id ? { ...post, text: post.original } : post)),
    );
  }, []);

  const copyPost = useCallback(async (post: GeneratedPost) => {
    const ok = await writeClipboard(post.text);
    if (copyTimer.current) clearTimeout(copyTimer.current);
    setCopyFlash({ id: post.id, ok });
    copyTimer.current = setTimeout(() => setCopyFlash(null), ok ? 1800 : 2600);
  }, []);

  const copyAll = useCallback(async () => {
    const payload = posts
      .map((post, index) => `--- ${index + 1}. ${post.label} ---\n\n${post.text}`)
      .join("\n\n\n");
    const ok = await writeClipboard(payload);
    if (allTimer.current) clearTimeout(allTimer.current);
    setAllFlash(ok);
    allTimer.current = setTimeout(() => setAllFlash(null), ok ? 1800 : 2600);
  }, [posts]);

  const downloadAll = useCallback(() => {
    const header = [
      "CastToLink — 10 LinkedIn posts",
      `Tone: ${tone.label}`,
      `Audience: ${audience.label}`,
      `Length: ${length.label}`,
      `Source: ${file?.name ?? "pasted transcript"}`,
      `Written by: ${usedModel ?? "offline templates"}`,
      "",
      "",
    ].join("\n");
    const body = posts
      .map(
        (post, index) =>
          `=== ${index + 1}. ${post.label} (${post.text.length} chars) ===\n\n${post.text}`,
      )
      .join("\n\n\n");
    const blob = new Blob([header + body], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "casttolink-posts.txt";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }, [audience.label, file, length.label, posts, tone.label, usedModel]);

  const avgChars = posts.length
    ? Math.round(posts.reduce((total, post) => total + post.text.length, 0) / posts.length)
    : 0;

  const engineValue = caps
    ? genState === "running"
      ? "Generating"
      : liveGeneration
        ? modelLabel(usedModel ?? caps.model)
        : "Demo mode"
    : "Checking";

  return (
    <div className="relative min-h-screen">
      <div className="grid-backdrop pointer-events-none absolute inset-x-0 top-0 h-[520px]" />
      <div className="pointer-events-none absolute left-1/2 top-[-180px] h-[420px] w-[820px] -translate-x-1/2 rounded-full bg-emerald-500/10 blur-[140px]" />
      <div className="pointer-events-none absolute right-[-120px] top-[120px] h-[360px] w-[520px] rounded-full bg-indigo-500/10 blur-[140px]" />

      {/* ------------------------------- Header ------------------------------- */}
      <header className="sticky top-0 z-40 border-b border-zinc-800/80 bg-zinc-950/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1500px] items-center gap-3 px-4 py-3 sm:px-6">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-400 to-indigo-500 shadow-lg shadow-emerald-500/20">
              <AudioLines className="h-5 w-5 text-zinc-950" strokeWidth={2.5} />
            </div>
            <div className="leading-tight">
              <div className="flex items-center gap-1.5">
                <span className="text-[15px] font-bold tracking-tight text-zinc-50">
                  CastToLink
                </span>
                <span className="rounded-md border border-emerald-400/25 bg-emerald-500/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-emerald-300">
                  Beta
                </span>
              </div>
              <span className="hidden text-[11px] text-zinc-500 sm:block">
                One episode &rarr; 10 LinkedIn posts
              </span>
            </div>
          </div>

          <div className="ml-auto flex items-center gap-2">
            <StatusPill
              icon={Activity}
              label="Engine"
              value={engineValue}
              tone={liveGeneration ? "good" : "muted"}
            />
            <StatusPill
              icon={Mic}
              label="Speech"
              value={liveTranscription ? modelLabel(caps?.speechModel ?? "") || "Deepgram" : "Simulated"}
              tone={liveTranscription ? "good" : "muted"}
            />
            {account ? (
              <StatusPill
                icon={Zap}
                label={account.plan === "pro" ? "Plan" : "Left"}
                value={
                  account.plan === "pro"
                    ? "Unlimited"
                    : `${account.quota.remaining ?? 0}/${account.quota.limit ?? 0}`
                }
                tone={account.quota.allowed ? "good" : "muted"}
              />
            ) : (
              <StatusPill icon={Zap} label="Runs" value={String(runs)} />
            )}

            {session ? (
              <button
                type="button"
                onClick={() => supabase?.auth.signOut()}
                title={account?.email ?? "Sign out"}
                className="flex items-center gap-1.5 rounded-xl border border-zinc-800 bg-zinc-900 px-3 py-2 text-[13px] font-semibold text-zinc-300 transition hover:border-zinc-700 hover:text-zinc-100"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline">Sign out</span>
              </button>
            ) : null}

            <button
              type="button"
              onClick={upgrade}
              className="flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-emerald-400 to-emerald-500 px-3 py-2 text-[13px] font-bold text-zinc-950 shadow-lg shadow-emerald-500/20 transition hover:from-emerald-300 hover:to-emerald-400"
            >
              <Crown className="h-4 w-4" />
              <span className="hidden sm:inline">Upgrade</span>
            </button>
          </div>
        </div>

        {bannerOpen ? (
          <div className="border-t border-emerald-400/10 bg-gradient-to-r from-emerald-500/10 via-indigo-500/10 to-transparent">
            <div className="mx-auto flex max-w-[1500px] items-center gap-3 px-4 py-2 sm:px-6">
              <Sparkles className="h-4 w-4 shrink-0 text-emerald-300" />
              <p className="text-[12.5px] text-zinc-300">
                {account?.plan === "pro" ? (
                  <>
                    <span className="font-semibold text-zinc-100">Pro:</span> unlimited
                    generations.{" "}
                  </>
                ) : account ? (
                  <>
                    <span className="font-semibold text-zinc-100">Free plan:</span>{" "}
                    {account.quota.remaining} of {account.quota.limit} generations left this
                    month.{" "}
                  </>
                ) : (
                  <>
                    <span className="font-semibold text-zinc-100">Free plan:</span> three
                    generations a month, after one emailed code.{" "}
                  </>
                )}
                <span className="hidden sm:inline">
                  Pro unlocks unlimited episodes, longer transcripts and scheduled posting &mdash;
                </span>{" "}
                <button
                  type="button"
                  onClick={upgrade}
                  className="inline-flex items-center gap-1 font-semibold text-emerald-300 underline decoration-emerald-400/40 underline-offset-2 transition hover:text-emerald-200"
                >
                  upgrade for $19/mo
                  <ArrowRight className="h-3 w-3" />
                </button>
              </p>
              <button
                type="button"
                onClick={() => setBannerOpen(false)}
                aria-label="Dismiss upgrade banner"
                className="ml-auto rounded-md p-1 text-zinc-500 transition hover:bg-zinc-800/60 hover:text-zinc-300"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        ) : null}
      </header>

      {billingOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/80 p-4 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-2xl border border-zinc-800 bg-zinc-900 p-5 shadow-2xl">
            <div className="mb-1 flex items-center gap-2">
              <Crown className="h-4 w-4 text-emerald-300" />
              <h2 className="text-sm font-semibold text-zinc-100">Upgrade to Pro</h2>
              <button
                type="button"
                onClick={() => setBillingOpen(false)}
                aria-label="Close"
                className="ml-auto rounded-md p-1 text-zinc-500 transition hover:bg-zinc-800 hover:text-zinc-300"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="mb-4 text-[12.5px] leading-relaxed text-zinc-400">
              Unlimited generations, billed monthly and cancellable whenever you like.
            </p>

            <div className="space-y-2">
              <button
                type="button"
                onClick={() => payWith("payfast")}
                disabled={billingBusy !== null}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-emerald-400 to-emerald-500 px-4 py-2.5 text-sm font-bold text-zinc-950 transition hover:brightness-110 disabled:opacity-60"
              >
                {billingBusy === "payfast" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <CreditCard className="h-4 w-4" />
                )}
                Pay in rand &mdash; PayFast
              </button>
              <button
                type="button"
                onClick={() => payWith("paypal")}
                disabled={billingBusy !== null}
                className="flex w-full items-center justify-center gap-2 rounded-xl border border-zinc-700 bg-zinc-800 px-4 py-2.5 text-sm font-semibold text-zinc-100 transition hover:border-zinc-600 disabled:opacity-60"
              >
                {billingBusy === "paypal" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Globe className="h-4 w-4" />
                )}
                Pay in dollars &mdash; PayPal
              </button>
            </div>

            <p className="mt-3 text-center text-[11px] text-zinc-600">
              You&rsquo;ll finish on {billingBusy === "paypal" ? "PayPal" : "the provider"}&rsquo;s
              own secure page.
            </p>
          </div>
        </div>
      ) : null}

      {/* -------------------------------- Main -------------------------------- */}
      <main className="relative mx-auto max-w-[1500px] px-4 py-6 sm:px-6 sm:py-8">
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,420px)_minmax(0,1fr)]">
          {/* ----------------------------- Left column ---------------------------- */}
          <div className="space-y-4 xl:sticky xl:top-[132px] xl:self-start">
            {supabase && !session ? <SignIn supabase={supabase} /> : null}

            <section className="rounded-2xl border border-zinc-800/80 bg-zinc-900/40 p-4 backdrop-blur-sm sm:p-5">
              <div className="mb-4 flex items-center gap-2">
                <span className="flex h-6 w-6 items-center justify-center rounded-md bg-zinc-800 font-mono text-[11px] text-zinc-400">
                  1
                </span>
                <h2 className="text-sm font-semibold text-zinc-100">Source audio</h2>
                <span className="ml-auto font-mono text-[10px] uppercase tracking-wider text-zinc-600">
                  mp3 &middot; wav
                </span>
              </div>

              {file ? (
                <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-emerald-400/20 bg-emerald-500/10">
                      <FileAudio className="h-5 w-5 text-emerald-300" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-zinc-100">{file.name}</p>
                      <p className="mt-0.5 font-mono text-[11px] text-zinc-500">
                        {formatBytes(file.size)} &middot;{" "}
                        {uploadState === "uploading"
                          ? `uploading ${Math.round(progress)}%`
                          : uploadState === "transcribing"
                            ? "transcribing"
                            : `transcript ready · ${transcriptWords.toLocaleString()} words`}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={clearFile}
                      aria-label="Remove file"
                      className="rounded-md p-1 text-zinc-500 transition hover:bg-zinc-800 hover:text-zinc-300"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="mt-3.5 h-1.5 overflow-hidden rounded-full bg-zinc-800">
                    <div
                      className={`h-full rounded-full bg-gradient-to-r from-emerald-400 to-indigo-400 transition-all duration-200 ${
                        uploadState === "transcribing" ? "animate-pulse-ring" : ""
                      }`}
                      style={{ width: `${progress}%` }}
                    />
                  </div>

                  <div className="mt-2.5 flex items-start gap-1.5">
                    {busy ? (
                      <>
                        <Loader2 className="mt-0.5 h-3.5 w-3.5 shrink-0 animate-spin text-emerald-400" />
                        <span className="text-[11px] text-zinc-400">
                          {uploadState === "uploading"
                            ? "Uploading the episode…"
                            : `Transcribing with ${caps?.speechModel ?? "Deepgram"}… long episodes take a minute.`}
                        </span>
                      </>
                    ) : (
                      <>
                        <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-400" />
                        <span className="text-[11px] text-zinc-400">
                          {transcriptSource === "audio" ? (
                            <>
                              Transcribed with{" "}
                              <span className="text-emerald-300">Deepgram</span>.
                            </>
                          ) : (
                            <>
                              Transcript loaded &mdash;{" "}
                              <span className="text-amber-300/90">simulated</span>, because no
                              speech-to-text key is configured.
                            </>
                          )}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div
                  onDragOver={(event) => {
                    event.preventDefault();
                    setDragging(true);
                  }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={(event) => {
                    event.preventDefault();
                    setDragging(false);
                    const dropped = event.dataTransfer.files?.[0];
                    if (dropped) startUpload(dropped);
                  }}
                  onClick={() => inputRef.current?.click()}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      inputRef.current?.click();
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  className={`group flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-5 py-10 text-center transition ${
                    dragging
                      ? "border-emerald-400/70 bg-emerald-500/10"
                      : "border-zinc-800 bg-zinc-950/40 hover:border-zinc-700 hover:bg-zinc-950/70"
                  }`}
                >
                  <div
                    className={`mb-3 flex h-12 w-12 items-center justify-center rounded-xl border transition ${
                      dragging
                        ? "border-emerald-400/40 bg-emerald-500/20"
                        : "border-zinc-800 bg-zinc-900 group-hover:border-zinc-700"
                    }`}
                  >
                    <Upload
                      className={`h-5 w-5 transition ${
                        dragging ? "text-emerald-300" : "text-zinc-400 group-hover:text-zinc-200"
                      }`}
                    />
                  </div>
                  <p className="text-sm font-medium text-zinc-200">
                    {dragging ? "Drop the episode to start" : "Drag & drop your episode"}
                  </p>
                  <p className="mt-1 text-xs text-zinc-500">
                    or{" "}
                    <span className="text-emerald-300 underline underline-offset-2">
                      browse files
                    </span>{" "}
                    &middot; .mp3, .wav
                  </p>
                  {caps && !liveTranscription ? (
                    <p className="mt-2.5 text-[11px] text-amber-300/80">
                      No speech-to-text key configured &mdash; uploads load the sample transcript.
                    </p>
                  ) : null}
                  <input
                    ref={inputRef}
                    type="file"
                    accept=".mp3,.wav,.m4a,audio/*"
                    className="hidden"
                    onChange={(event) => {
                      const selected = event.target.files?.[0];
                      if (selected) startUpload(selected);
                    }}
                  />
                </div>
              )}

              {fileError ? (
                <p className="mt-3 flex items-start gap-1.5 rounded-lg border border-rose-500/20 bg-rose-500/10 px-3 py-2 text-[11.5px] text-rose-300">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  {fileError}
                </p>
              ) : null}
            </section>

            <section className="overflow-hidden rounded-2xl border border-zinc-800/80 bg-zinc-900/40 backdrop-blur-sm">
              <button
                type="button"
                onClick={() => setPasteOpen((open) => !open)}
                aria-expanded={pasteOpen}
                className="flex w-full items-center gap-2.5 px-4 py-3.5 text-left transition hover:bg-zinc-900/60 sm:px-5"
              >
                <FileText className="h-4 w-4 shrink-0 text-indigo-300" />
                <span className="text-sm font-semibold text-zinc-100">Paste a transcript</span>
                <span className="rounded-md border border-indigo-400/20 bg-indigo-500/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-indigo-300">
                  Instant
                </span>
                {transcriptWords > 0 ? (
                  <span className="hidden font-mono text-[10px] text-zinc-500 sm:inline">
                    {transcriptWords.toLocaleString()} words
                  </span>
                ) : null}
                <ChevronDown
                  className={`ml-auto h-4 w-4 shrink-0 text-zinc-500 transition-transform duration-300 ${
                    pasteOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              <div
                className={`grid transition-all duration-300 ease-out ${
                  pasteOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                }`}
              >
                <div className="overflow-hidden">
                  <div className="space-y-3 border-t border-zinc-800/60 px-4 py-4 sm:px-5">
                    <textarea
                      value={transcript}
                      onChange={(event) => {
                        setTranscript(event.target.value);
                        setTranscriptSource(event.target.value ? "paste" : "none");
                      }}
                      placeholder="Paste your episode transcript, show notes or raw interview text here. 40+ words is enough for a usable set of drafts."
                      className="h-44 w-full resize-y rounded-xl border border-zinc-800 bg-zinc-950/70 p-3.5 text-[13px] leading-relaxed text-zinc-200 outline-none transition placeholder:text-zinc-600 focus:border-indigo-500/50 focus:ring-2 focus:ring-indigo-500/20"
                    />
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setTranscript(SAMPLE_TRANSCRIPT);
                          setTranscriptSource("sample");
                        }}
                        className="flex items-center gap-1.5 rounded-lg border border-zinc-800 bg-zinc-900 px-2.5 py-1.5 text-[11.5px] font-medium text-zinc-300 transition hover:border-zinc-700 hover:text-zinc-100"
                      >
                        <Wand2 className="h-3.5 w-3.5 text-indigo-300" />
                        Load sample episode
                      </button>
                      {transcript ? (
                        <button
                          type="button"
                          onClick={() => {
                            setTranscript("");
                            setTranscriptSource("none");
                          }}
                          className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11.5px] font-medium text-zinc-500 transition hover:bg-zinc-800/60 hover:text-zinc-300"
                        >
                          <X className="h-3.5 w-3.5" />
                          Clear
                        </button>
                      ) : null}
                      <span className="ml-auto font-mono text-[11px] text-zinc-500">
                        {transcriptWords.toLocaleString()} words
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="rounded-2xl border border-zinc-800/80 bg-zinc-900/40 p-4 backdrop-blur-sm sm:p-5">
              <div className="mb-4 flex items-center gap-2">
                <span className="flex h-6 w-6 items-center justify-center rounded-md bg-zinc-800 font-mono text-[11px] text-zinc-400">
                  2
                </span>
                <h2 className="text-sm font-semibold text-zinc-100">Voice &amp; targeting</h2>
              </div>

              <div className="space-y-3.5">
                <Select<ToneId>
                  icon={Sparkles}
                  label="Target audience tone"
                  hint={tone.blurb}
                  value={toneId}
                  options={TONES.map((item) => ({ id: item.id, label: item.label }))}
                  onChange={setToneId}
                />
                <Select<AudienceId>
                  icon={Users}
                  label="Who is reading"
                  value={audienceId}
                  options={AUDIENCES.map((item) => ({ id: item.id, label: item.label }))}
                  onChange={setAudienceId}
                />
                <Select<LengthId>
                  icon={Layers}
                  label="Post length"
                  value={lengthId}
                  options={LENGTHS.map((item) => ({ id: item.id, label: item.label }))}
                  onChange={setLengthId}
                />
              </div>

              <button
                type="button"
                onClick={runGeneration}
                disabled={!canGenerate}
                className={`mt-5 flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3.5 text-sm font-bold transition ${
                  canGenerate
                    ? "bg-gradient-to-r from-emerald-400 via-emerald-500 to-indigo-500 text-zinc-950 shadow-lg shadow-emerald-500/20 hover:brightness-110 active:scale-[0.99]"
                    : "cursor-not-allowed border border-zinc-800 bg-zinc-900 text-zinc-600"
                }`}
              >
                {genState === "running" ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Generating 10 posts&hellip;
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" />
                    {posts.length ? "Regenerate 10 posts" : "Generate 10 LinkedIn posts"}
                  </>
                )}
              </button>

              {!hasSource ? (
                <p className="mt-2.5 flex items-center justify-center gap-1.5 text-center text-[11.5px] text-zinc-500">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-400/80" />
                  Add audio or paste 40+ words to unlock generation
                </p>
              ) : liveGeneration && !session ? (
                <p className="mt-2.5 flex items-center justify-center gap-1.5 text-center text-[11.5px] text-zinc-500">
                  <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-emerald-400/80" />
                  Sign in above to generate &mdash; one emailed code, no password
                </p>
              ) : liveGeneration ? (
                <p className="mt-2.5 flex items-center justify-center gap-1.5 text-center text-[11.5px] text-zinc-500">
                  <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-emerald-400/80" />
                  Written by {modelLabel(caps?.model ?? "")} &mdash; saved to your account
                </p>
              ) : (
                <p className="mt-2.5 flex items-center justify-center gap-1.5 text-center text-[11.5px] text-zinc-500">
                  <WifiOff className="h-3.5 w-3.5 shrink-0 text-amber-400/80" />
                  Demo mode &mdash; drafts come from the offline templates
                </p>
              )}
            </section>
          </div>

          {/* ---------------------------- Right column ---------------------------- */}
          <div className="min-w-0">
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <div>
                <h2 className="flex items-center gap-2 text-base font-semibold text-zinc-100">
                  <Layers className="h-4 w-4 text-emerald-300" />
                  Generated posts
                  {posts.length ? (
                    <span className="rounded-md border border-zinc-800 bg-zinc-900 px-2 py-0.5 font-mono text-[11px] text-zinc-400">
                      {posts.length}
                    </span>
                  ) : null}
                </h2>
                <p className="mt-0.5 text-xs text-zinc-500">
                  {posts.length
                    ? `${tone.label} · ${audience.label} · avg ${avgChars.toLocaleString()} chars${
                        usedModel ? ` · ${modelLabel(usedModel)}` : ""
                      }`
                    : "Ten native LinkedIn formats, each taking a different angle on your episode"}
                </p>
              </div>

              {posts.length ? (
                <div className="ml-auto flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={copyAll}
                    className={`flex items-center gap-1.5 rounded-xl border px-3 py-2 text-[12px] font-semibold transition ${
                      allFlash === true
                        ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-300"
                        : allFlash === false
                          ? "border-rose-500/40 bg-rose-500/15 text-rose-300"
                          : "border-zinc-800 bg-zinc-900 text-zinc-300 hover:border-zinc-700 hover:text-zinc-100"
                    }`}
                  >
                    {allFlash === true ? (
                      <Check className="h-3.5 w-3.5" />
                    ) : allFlash === false ? (
                      <AlertTriangle className="h-3.5 w-3.5" />
                    ) : (
                      <Copy className="h-3.5 w-3.5" />
                    )}
                    {allFlash === true
                      ? "Copied all!"
                      : allFlash === false
                        ? "Clipboard blocked"
                        : "Copy all"}
                  </button>
                  <button
                    type="button"
                    onClick={downloadAll}
                    className="flex items-center gap-1.5 rounded-xl border border-zinc-800 bg-zinc-900 px-3 py-2 text-[12px] font-semibold text-zinc-300 transition hover:border-zinc-700 hover:text-zinc-100"
                  >
                    <Download className="h-3.5 w-3.5" />
                    .txt
                  </button>
                  <button
                    type="button"
                    onClick={runGeneration}
                    disabled={!canGenerate}
                    className="flex items-center gap-1.5 rounded-xl border border-zinc-800 bg-zinc-900 px-3 py-2 text-[12px] font-semibold text-zinc-300 transition hover:border-zinc-700 hover:text-zinc-100 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                    Regenerate
                  </button>
                </div>
              ) : null}
            </div>

            {notice ? (
              <div
                className={`mb-4 flex items-start gap-2 rounded-xl border px-4 py-3 text-[12.5px] ${
                  notice.kind === "warn"
                    ? "border-amber-400/25 bg-amber-500/10 text-amber-200"
                    : "border-indigo-400/25 bg-indigo-500/10 text-indigo-200"
                }`}
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <p className="flex-1">{notice.text}</p>
                <button
                  type="button"
                  onClick={() => setNotice(null)}
                  aria-label="Dismiss notice"
                  className="rounded-md p-0.5 opacity-70 transition hover:opacity-100"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : null}

            {genState === "running" ? (
              <div className="space-y-4">
                <div className="rounded-2xl border border-zinc-800/80 bg-zinc-900/40 p-4 sm:p-5">
                  <div className="flex items-center gap-2.5">
                    <Loader2 className="h-4 w-4 animate-spin text-emerald-400" />
                    <span className="text-sm font-medium text-zinc-200">{stages[stage]}</span>
                    <span className="ml-auto font-mono text-[11px] text-zinc-500">
                      {stage + 1}/{stages.length}
                    </span>
                  </div>
                  <div className="mt-3.5 h-1.5 overflow-hidden rounded-full bg-zinc-800">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-indigo-400 transition-all duration-500"
                      style={{ width: `${((stage + 1) / stages.length) * 100}%` }}
                    />
                  </div>
                  <ul className="mt-4 space-y-1.5">
                    {stages.map((item, index) => (
                      <li key={item} className="flex items-center gap-2 text-[11.5px]">
                        {index < stage ? (
                          <Check className="h-3.5 w-3.5 shrink-0 text-emerald-400" />
                        ) : index === stage ? (
                          <span className="h-3.5 w-3.5 shrink-0 animate-pulse-ring rounded-full border-2 border-emerald-400" />
                        ) : (
                          <span className="h-3.5 w-3.5 shrink-0 rounded-full border border-zinc-700" />
                        )}
                        <span className={index <= stage ? "text-zinc-300" : "text-zinc-600"}>
                          {item}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
                {[0, 1, 2].map((index) => (
                  <SkeletonCard key={index} delay={index * 120} />
                ))}
              </div>
            ) : posts.length ? (
              <div className="space-y-4">
                {posts.map((post) => (
                  <PostCard
                    key={post.id}
                    post={post}
                    isEditing={editingId === post.id}
                    flash={copyFlash?.id === post.id ? (copyFlash.ok ? "ok" : "fail") : null}
                    showOrigin={liveGeneration}
                    onEditToggle={() =>
                      setEditingId((current) => (current === post.id ? null : post.id))
                    }
                    onChange={(value) => updatePost(post.id, value)}
                    onReset={() => resetPost(post.id)}
                    onCopy={() => copyPost(post)}
                  />
                ))}

                <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-dashed border-zinc-800 bg-zinc-900/30 px-5 py-4">
                  <TrendingUp className="h-4 w-4 shrink-0 text-emerald-300" />
                  <p className="text-[12.5px] text-zinc-400">
                    Post one or two of these per day, in this order, for the best reach. Pro adds
                    scheduling, A/B hooks and comment-reply drafts.
                  </p>
                  <button
                    type="button"
                    onClick={upgrade}
                    className="ml-auto flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-emerald-400 to-emerald-500 px-3 py-2 text-[12px] font-bold text-zinc-950 transition hover:brightness-110"
                  >
                    <Crown className="h-3.5 w-3.5" />
                    Go Pro
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex min-h-[520px] flex-col items-center justify-center rounded-2xl border border-dashed border-zinc-800 bg-zinc-900/20 px-6 py-14 text-center">
                <div className="relative mb-6">
                  <div className="absolute inset-0 animate-pulse-ring rounded-2xl bg-emerald-500/20 blur-2xl" />
                  <div className="relative flex h-16 w-16 items-center justify-center rounded-2xl border border-zinc-800 bg-zinc-900">
                    <Lightbulb className="h-7 w-7 text-zinc-600" />
                  </div>
                </div>
                <h3 className="text-base font-semibold text-zinc-200">Nothing generated yet</h3>
                <p className="mt-1.5 max-w-sm text-[13px] leading-relaxed text-zinc-500">
                  Drop an episode or paste a transcript on the left, pick a tone, and CastToLink
                  drafts ten posts &mdash; each in a different native LinkedIn format.
                </p>

                <div className="mt-7 grid w-full max-w-lg grid-cols-1 gap-2 sm:grid-cols-2">
                  {ARCHETYPES.slice(0, 6).map((archetype) => {
                    const accent = ACCENTS[archetype.accent];
                    const Icon = ICONS[archetype.id];
                    return (
                      <div
                        key={archetype.id}
                        title={archetype.note}
                        className="flex items-center gap-2.5 rounded-xl border border-zinc-800/80 bg-zinc-900/40 px-3 py-2.5 text-left"
                      >
                        <span
                          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border ${accent.chip} ${accent.border}`}
                        >
                          <Icon className={`h-3.5 w-3.5 ${accent.text}`} />
                        </span>
                        <span className="truncate text-[12px] font-medium text-zinc-400">
                          {archetype.label}
                        </span>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-7 flex flex-wrap items-center justify-center gap-4 text-[11.5px] text-zinc-600">
                  <span className="flex items-center gap-1.5">
                    <Target className="h-3.5 w-3.5" />
                    Under 1,300 characters
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Pencil className="h-3.5 w-3.5" />
                    Inline editing
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Copy className="h-3.5 w-3.5" />
                    One-click copy
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        <footer className="mt-10 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-zinc-800/60 pt-5 text-[11.5px] text-zinc-600">
          <span className="font-semibold text-zinc-500">CastToLink</span>
          <span>
            {liveGeneration
              ? `Transcription by Deepgram, posts written by ${modelLabel(caps?.model ?? "")}.`
              : "Running in demo mode: add API keys to generate with Claude."}
          </span>
          {usage ? (
            <span className="font-mono">
              {usage.inputTokens.toLocaleString()} in / {usage.outputTokens.toLocaleString()} out
            </span>
          ) : null}
          <span className="ml-auto flex items-center gap-1.5">
            <Gauge className="h-3.5 w-3.5" />
            {posts.length ? `${posts.length} drafts · avg ${avgChars} chars` : "Awaiting input"}
          </span>
        </footer>
      </main>
    </div>
  );
}
